/**
 * @file 分析 — 取り込んだ「計画のもと」から、出題の傾向と計画とのつり合いを読む面。
 *
 * @what: 3 つを出す。(1) どの領域が多く問われているか (2) 単元ごとの出題数と到達計画の
 *   対比 (3) 学校ごとの考査の構成。各行から実際の出題を開ける。行は単元ではなく語彙
 *   (`分野/単元`) 単位で、1 つの力を段ごとに分けて持つモデルの都合で同じ問が重複しないようにする。
 * @why: 入試の出題は目標ではなく物差しである (代表 2026-08-22)。この面は分布に時間配分を
 *   合わせるためではなく、計画の穴を見つけるために読む。数字は取り込んだ資料から毎回数える
 *   ので、本を足しても古くならない。
 * @failure: 資料が無い / 出題が無いときは、何をすれば出るようになるかだけを出す。
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { StudyCorpusState } from "../hooks/useStudyCorpus";
import { analyzeCorpus, type VocabCalibration } from "../model/corpusAnalysis";
import { levelById, type AttainmentModel } from "../model";
import { domainColor } from "./domainColors";
import "./study.css";

/** 最初に見せる語彙の数。これを超えた分はボタンで開く。 */
const VOCAB_ROWS = 12;

function LevelTrack({ model, cal }: { model: AttainmentModel; cal: VocabCalibration }) {
  return (
    <span className="study-level-track">
      {model.stages.map((s) => {
        const lv = cal.levelByStage[s.id];
        const level = lv ? levelById(model, lv) : null;
        return (
          <span
            key={s.id}
            className={`study-level-cell${level ? ` study-level-cell--${level.id}` : ""}`}
            title={level ? `${s.period}: ${level.label}` : `${s.period}: 取り組まない`}
          >
            <span className="study-visually-hidden">
              {s.period}
              {level ? level.label : "取り組まない"}
            </span>
          </span>
        );
      })}
    </span>
  );
}

export function AnalysisPanel({
  model,
  corpusState,
  sourceHrefBase,
}: {
  model: AttainmentModel;
  corpusState: StudyCorpusState;
  /** リーダーへの基点 (`/study/{familyId}/sources`)。 */
  sourceHrefBase: string;
}) {
  const [allUnits, setAllUnits] = useState(false);
  const analysis = useMemo(
    () => (corpusState.status === "ready" ? analyzeCorpus(model, corpusState.corpus.entries) : null),
    [model, corpusState],
  );

  if (corpusState.status === "loading" || corpusState.status === "idle") {
    return (
      <p role="status" className="study-muted">
        取り込んだ内容を読み込んでいます...
      </p>
    );
  }
  if (corpusState.status === "error") {
    return (
      <p role="alert" className="study-error">
        取り込んだ内容を読み込めませんでした。時間を置いて再読み込みしてください。
      </p>
    );
  }
  if (!analysis || analysis.totalItems === 0) {
    return (
      <p className="study-muted">
        まだ読み解くものがありません。本だなに「計画のもと」を取り込むと、何がどれくらい問われて
        いるかをここで見られます。
      </p>
    );
  }

  const maxDomain = Math.max(...analysis.byDomain.map((d) => d.items), 1);
  const maxVocab = Math.max(...analysis.vocab.map((v) => v.items), 1);
  const shownVocab = allUnits ? analysis.vocab : analysis.vocab.slice(0, VOCAB_ROWS);
  const firstSource = corpusState.corpus.entries[0]?.sourceId ?? "";
  // filterValue は `u:{unitId}` か `t:{分野/単元}`。リーダーのしぼりこみと同じ形にする。
  const vocabHref = (v: VocabCalibration) => {
    const base = `${sourceHrefBase}/${encodeURIComponent(firstSource)}`;
    return v.filterValue.startsWith("u:")
      ? `${base}?unit=${encodeURIComponent(v.filterValue.slice(2))}`
      : `${base}?tax=${encodeURIComponent(v.filterValue.slice(2))}`;
  };
  const schoolHref = (school: string) =>
    `${sourceHrefBase}/${encodeURIComponent(firstSource)}?school=${encodeURIComponent(school)}`;

  return (
    <div className="study-analysis">
      <p className="study-analysis-lede">
        {`${analysis.sourceTitles.join("・")} から、${analysis.schoolCount} 校 ${analysis.totalItems} 問を読み解いています。ここに出る数は、取り込んだものを毎回数えたものです。`}
      </p>

      <section className="study-analysis-block">
        <h3 className="study-subsection-title">何がよく問われているか</h3>
        <p className="study-section-help">
          領域は計画の面と同じ分け方です。1 つの問がいくつかの領域にまたがることがあります。
        </p>
        <ul className="study-bars">
          {analysis.byDomain.map((d) => {
            const color = domainColor(d.domainId);
            return (
              <li key={d.domainId} className="study-bar-row">
                <span className="study-bar-label">{d.label}</span>
                <span className="study-bar-track">
                  <span
                    className="study-bar"
                    style={{
                      width: `${Math.max(2, Math.round((d.items / maxDomain) * 100))}%`,
                      ...(color ? { background: color } : {}),
                    }}
                  />
                </span>
                <span className="study-bar-value">{d.items}</span>
              </li>
            );
          })}
        </ul>
      </section>

      <section className="study-analysis-block">
        <h3 className="study-subsection-title">出題と、いまの計画のつり合い</h3>
        <p className="study-section-help">
          出題が多いのに、最後まで「仕上げ」に届かない単元には印を付けています。入試のためではなく、
          その先で使う力かどうかで判断してください。集団や運動は問題を解いて測るものではないので、
          機会があったかで見ます。
        </p>
        <div className="study-table-scroll">
          <table className="study-analysis-table">
            <thead>
              <tr>
                <th scope="col">単元（計画で扱う順）</th>
                <th scope="col" className="study-num">
                  出題
                </th>
                <th scope="col">計画（入園前 → 年長）</th>
                <th scope="col">
                  <span className="study-visually-hidden">出題を見る</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {shownVocab.map((v) => (
                <tr key={v.key}>
                  <th scope="row">
                    <span className="study-analysis-unit">
                      {v.label}
                      <span className="study-analysis-field">{v.field}</span>
                      {v.shortOfConsolidate ? (
                        <span className="study-gap-badge" title="最後まで仕上げに届かない">
                          練習どまり
                        </span>
                      ) : v.measuredByPlace ? (
                        <span
                          className="study-place-badge"
                          title="問題を解いて測るのではなく、機会があったかで見る"
                        >
                          場で見る
                        </span>
                      ) : null}
                    </span>
                    {v.unitLabels.length > 0 ? (
                      <span className="study-analysis-units">{v.unitLabels.join(" → ")}</span>
                    ) : (
                      <span className="study-analysis-units">計画にまだ無い</span>
                    )}
                  </th>
                  <td className="study-num">
                    <span className="study-bar-track study-bar-track--inline">
                      <span
                        className="study-bar study-bar--plain"
                        style={{ width: `${Math.max(2, Math.round((v.items / maxVocab) * 100))}%` }}
                      />
                    </span>
                    {v.items}
                  </td>
                  <td>
                    <LevelTrack model={model} cal={v} />
                  </td>
                  <td>
                    <Link to={vocabHref(v)}>見る</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {analysis.vocab.length > VOCAB_ROWS ? (
          <button
            type="button"
            className="study-ghost-btn"
            onClick={() => setAllUnits((v) => !v)}
            aria-expanded={allUnits}
          >
            {allUnits
              ? "出題の多い順に 12 件だけ表示する"
              : `のこり ${analysis.vocab.length - VOCAB_ROWS} 種類も見る`}
          </button>
        ) : null}
        {analysis.vocabularyWithoutItems.length > 0 ? (
          <p className="study-analysis-note">
            計画にはあるが、取り込んだ中に出題が見当たらない単元:{" "}
            {analysis.vocabularyWithoutItems.join("・")}
          </p>
        ) : null}
      </section>

      <section className="study-analysis-block">
        <h3 className="study-subsection-title">学校によって問い方が違う</h3>
        <p className="study-section-help">
          紙に書く問題ばかりではありません。何種類の単元を問うか（広さ）と、紙とそれ以外の割合を
          並べています。
        </p>
        <div className="study-table-scroll">
          <table className="study-analysis-table study-analysis-table--schools">
            <thead>
              <tr>
                <th scope="col">学校</th>
                <th scope="col" className="study-num">
                  問数
                </th>
                <th scope="col" className="study-num">
                  単元の広さ
                </th>
                <th scope="col">
                  紙<span aria-hidden="true"> / </span>それ以外
                </th>
                <th scope="col">
                  <span className="study-visually-hidden">出題を見る</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {analysis.schools.map((s) => (
                <tr key={s.school}>
                  <th scope="row">{s.school}</th>
                  <td className="study-num">{s.items}</td>
                  <td className="study-num">{s.unitKinds}</td>
                  <td>
                    <span
                      className="study-split-bar"
                      title={`紙 ${s.paper} 問 / それ以外 ${s.other} 問`}
                    >
                      <span
                        className="study-split-bar-paper"
                        style={{ width: `${Math.round((s.paper / s.items) * 100)}%` }}
                      />
                      <span
                        className="study-split-bar-other"
                        style={{ width: `${Math.round((s.other / s.items) * 100)}%` }}
                      />
                    </span>
                    <span className="study-split-bar-legend">
                      {s.paper} / {s.other}
                    </span>
                  </td>
                  <td>
                    <Link to={schoolHref(s.school)}>見る</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
